/**
 * The pre-flight briefs and safety meetings of a project on its Safety tab (R161; decisions 916, 917, 974;
 * docs/specs/safety-brief.md §6; help `cloud.safety-documents`). Briefs are made on Teslyn Mobile and printed by
 * Teslyn Field; here every staff role reads them, nobody changes them. The client sees each brief's PDF only while
 * the project's setting "Clients see each brief's PDF under Safety" is on (Set-up tab, "Pre-flight brief").
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BRIEF_ITEM_WORDS,
  BRIEF_KIND_WORDS,
  actionOwnerWords,
  briefsNewestFirst,
  briefsVisibleToClient,
  canReadBriefs,
  hazardWhereWords,
  notSignedYet,
  openActions,
  shortDate,
  signWords,
  signsOf,
  sizeText,
  uncheckedFor,
  type SafetyBrief,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import {
  briefPdfUrl,
  loadClientBriefs,
  loadProjectBriefs,
  type ClientBrief,
  type ProjectBriefs,
} from './briefsApi.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** "07:42" in the browser's own time (the crew sign where the survey is; staff read it where they are). */
export const briefTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${shortDate(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const defaultOpen = (url: string): void => {
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) window.location.assign(url);
};

/** Staff: every brief and meeting of the project, read only. */
export function StaffBriefs({
  sb,
  project,
  roles,
  open = defaultOpen,
  time = briefTime,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
  readonly open?: (url: string) => void;
  readonly time?: (iso: string) => string;
}): ReactElement | null {
  const [data, setData] = useState<ProjectBriefs | null | 'missing'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    loadProjectBriefs(sb, project.id)
      .then((d) => setData(d ?? 'missing'))
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  if (!canReadBriefs(roles)) return null;
  // the database update with the briefs is not applied yet: nothing to say
  if (data === 'missing') return null;
  const title = 'Pre-flight briefs and safety meetings';
  if (error)
    return (
      <section className="panel stack" aria-label={title}>
        <h2 className="heading">{title}</h2>
        <ErrorState message={error} onRetry={reload} />
      </section>
    );
  if (!data) return <Loading what="Pre-flight briefs" />;
  const briefs = briefsNewestFirst(data.briefs);
  const open_ = openActions(data.actions);
  const visible = briefsVisibleToClient(data.setting);

  async function pdf(b: SafetyBrief): Promise<void> {
    const f = data && data !== 'missing' ? data.pdfs[b.id] : undefined;
    if (!f) return;
    setNotice(null);
    try {
      open(await briefPdfUrl(sb, f.object_path, b.pdf_name || f.file_name));
    } catch (e) {
      setNotice(message(e));
    }
  }

  return (
    <section className="panel stack" aria-label={title}>
      <h2 className="heading">{title}</h2>
      <p className="caption muted">
        Made on Teslyn Mobile by the crew and pilots, each person signing on their own phone; Teslyn Field
        makes each one’s PDF. Read only here.{' '}
        {visible
          ? 'The client sees each brief’s PDF under Safety (Set-up tab, Pre-flight brief).'
          : 'The client does not see them (Set-up tab, Pre-flight brief, to change it).'}
      </p>
      {notice && (
        <p className="notice notice--fail" role="alert">
          {notice}
        </p>
      )}
      {open_.length > 0 && (
        <div className="stack" aria-label="Open actions">
          <h3 className="label">Open actions</h3>
          <ul className="stack">
            {open_.map((a) => (
              <li key={a.id} className="body">
                {a.text} <span className="caption muted">({actionOwnerWords(a)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {briefs.length === 0 ? (
        <p className="body muted">
          No pre-flight brief or safety meeting yet. They are made on Teslyn Mobile.
        </p>
      ) : (
        <ul className="stack" aria-label="Briefs and meetings">
          {briefs.map((b) => {
            const signs = signsOf(data.signs, b.id);
            const missing = notSignedYet(b, data.signs);
            const pdfFile = data.pdfs[b.id];
            const label = `${BRIEF_KIND_WORDS[b.kind]} ${shortDate(b.brief_date)}`;
            return (
              <li key={b.id} className="panel stack" aria-label={label}>
                <div className="row row--wrap">
                  <div className="grow">
                    <p className="body">
                      {label}
                      {b.finished_at ? (
                        <span className="chip chip--pass"> Finished</span>
                      ) : (
                        <span className="chip chip--warn"> Not finished</span>
                      )}
                    </p>
                    <p className="caption muted">
                      {[
                        b.leader_name ? `Led by ${b.leader_name}` : '',
                        b.location,
                        `opened ${time(b.opened_at)}`,
                        b.kind === 'safety_meeting' && b.next_meeting
                          ? `next meeting ${shortDate(b.next_meeting)}`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {pdfFile ? (
                    <Button variant="quiet" onClick={() => void pdf(b)} aria-label={`Open the PDF: ${label}`}>
                      Open the PDF
                    </Button>
                  ) : (
                    <span className="caption muted">No PDF yet</span>
                  )}
                </div>
                {pdfFile && (
                  <p className="caption muted">
                    {b.pdf_name || pdfFile.file_name}, {sizeText(pdfFile.size_bytes)}
                  </p>
                )}
                {b.snapshot.blocks.length > 0 && (
                  <p className="caption">
                    {b.snapshot.blocks.map((s) => `${s.name}: ${s.text}`).join(' · ')}
                  </p>
                )}
                {b.snapshot.finished.length > 0 && (
                  <p className="caption muted">{b.snapshot.finished.join(' · ')}</p>
                )}
                {b.hazards.length > 0 && (
                  <p className="caption">
                    {b.hazards
                      .map(
                        (h) =>
                          `${h.n} ${h.name} (${hazardWhereWords(h)}): ${h.briefed ? 'briefed' : 'not briefed'}`,
                      )
                      .join(' · ')}
                  </p>
                )}
                {b.items.length > 0 && (
                  <ul className="stack">
                    {b.items.map((i) => (
                      <li key={i.id} className="caption">
                        {BRIEF_ITEM_WORDS[i.kind]}: {i.text}
                      </li>
                    ))}
                  </ul>
                )}
                <ul className="stack" aria-label={`Signs: ${label}`}>
                  {signs.length === 0 && <li className="caption muted">Nobody has signed yet.</li>}
                  {signs.map((s) => {
                    const unticked = uncheckedFor(s, b);
                    return (
                      <li key={s.id} className="caption">
                        <span className="body">{s.name}</span>
                        {s.role ? ` (${s.role})` : ''}: {signWords(s, b, time)}
                        {unticked.length > 0 && (
                          <span className="chip chip--warn">
                            {' '}
                            Not ticked: {unticked.map((c) => c.text).join('; ')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                  {missing.length > 0 && (
                    <li className="caption muted">Not signed yet: {missing.map((a) => a.name).join(', ')}</li>
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** The client: the briefs' PDFs shown under Safety (decision 974.6); nothing while the project's setting is off. */
export function ClientBriefs({
  sb,
  project,
  open = defaultOpen,
  onLoaded,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly open?: (url: string) => void;
  /** How many there are once read (the tab says "nothing shared" only when there is nothing at all). */
  readonly onLoaded?: (n: number) => void;
}): ReactElement | null {
  const [briefs, setBriefs] = useState<ClientBrief[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadClientBriefs(sb, project.id)
      .then((b) => {
        if (!live) return;
        setBriefs(b);
        onLoaded?.(b.length);
      })
      .catch((e: unknown) => {
        if (live) setNotice(message(e));
      });
    return () => {
      live = false;
    };
  }, [sb, project.id, onLoaded]);

  if (notice)
    return (
      <p className="notice notice--fail" role="alert">
        {notice}
      </p>
    );
  if (!briefs || briefs.length === 0) return null;
  const title = 'Pre-flight briefs and safety meetings';
  return (
    <section className="stack" aria-label={title}>
      <h2 className="heading">{title}</h2>
      <ul className="stack">
        {briefs.map((b) => {
          const label = `${BRIEF_KIND_WORDS[b.kind]} ${shortDate(b.brief_date)}`;
          return (
            <li key={b.id} className="panel row row--wrap">
              <div className="grow">
                <p className="body">{label}</p>
                <p className="caption muted">
                  {b.file_name}
                  {b.size_bytes ? `, ${sizeText(b.size_bytes)}` : ''}.
                </p>
              </div>
              <Button
                variant="quiet"
                aria-label={`Open ${label}`}
                onClick={() =>
                  void briefPdfUrl(sb, b.storage_path, b.file_name)
                    .then(open)
                    .catch((e: unknown) => setNotice(message(e)))
                }
              >
                Open
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
